/**
 * AB mobile filter panel (collection + search), < 1024px only.
 * Works on the markup from snippets/filter-sort.liquid (.ab-filter-panel).
 *
 * - Only Sort By starts open; every other category (Size included) starts collapsed. Rows the
 *   shopper opens / closes keep that state when the section re-renders.
 * - Batch apply: picking options no longer reloads the grid. Selections stay pending until
 *   "Apply filters" (the bottom button); closing the panel without applying discards them.
 *   Reset / Remove all still apply right away.
 * - Gendered collections: one size grid in the collection's own sizing. Unisex values
 *   ("W10.5/M9") are merged into the matching men's (9) or women's (10.5) size, so tapping 9 on
 *   Men's selects both "9" and "W10.5/M9".
 * - Picks show their selected state immediately; a collapsed row shows what is selected inside it.
 * - While the panel is open: <html class="ab-filter-open"> (lifts the panel above the header and
 *   the sticky filter bar) and floating widgets near the bottom of the screen are hidden.
 * Desktop is left alone (every category open, instant apply, original size grid).
 */
(function () {
  if (window.__abFilterMobile) return;
  window.__abFilterMobile = true;

  var mobile = window.matchMedia('(max-width: 1023px)');
  var PANEL = '.ab-filter-panel';
  var SORT_KEY = 'sort by';
  var SIZE_KEY = 'size';
  var APPLY_LABEL = 'Apply filters';

  var userOpen = new Set([SORT_KEY]);
  var initial = new WeakMap(); // input -> state as rendered by the server
  var panelOpen = false;
  var submitted = false;
  var queued = false;

  /* ---------- helpers ---------- */

  function keyOf(details) {
    var label = details.querySelector(':scope > summary .text-base > span');
    return label ? label.textContent.trim().toLowerCase() : '';
  }

  function findDetails(panel, key) {
    var list = panel.querySelectorAll(':scope > details');
    for (var i = 0; i < list.length; i++) if (keyOf(list[i]) === key) return list[i];
    return null;
  }

  function labelText(input) {
    var label = input.closest('label') || (input.id && document.querySelector('label[for="' + CSS.escape(input.id) + '"]'));
    if (!label) return input.value;
    var parts = Array.prototype.map
      .call(label.querySelectorAll('span'), function (s) {
        return s.textContent.trim();
      })
      .filter(Boolean);
    var text = parts.length ? parts.join('/') : label.textContent;
    return text.replace(/\s+/g, ' ').trim();
  }

  function stateOf(input) {
    return input.type === 'checkbox' || input.type === 'radio' ? input.checked : input.value;
  }

  function snapshot(panel) {
    panel.querySelectorAll('input').forEach(function (input) {
      if (!initial.has(input)) initial.set(input, stateOf(input));
    });
  }

  function isDirty(panel) {
    var inputs = panel.querySelectorAll('input');
    for (var i = 0; i < inputs.length; i++) {
      if (initial.has(inputs[i]) && initial.get(inputs[i]) !== stateOf(inputs[i])) return true;
    }
    return false;
  }

  function discard(panel) {
    panel.querySelectorAll('input').forEach(function (input) {
      if (!initial.has(input)) return;
      var value = initial.get(input);
      if (input.type === 'checkbox' || input.type === 'radio') input.checked = value;
      else input.value = value;
    });
  }

  /* ---------- size grid (gendered collections) ---------- */

  // Grouped filter values (Shopify filter setting groups, e.g. the UK store) submit an id
  // ("gid://shopify/FilterSettingGroup/…"); the size itself is the label ("9").
  function sizeValue(input) {
    var v = String(input.value).trim();
    return /^gid:\/\//.test(v) ? labelText(input) : v;
  }

  function sizeKey(value, gender) {
    var v = String(value).trim();
    var unisex = /^W\s*([\d.]+)\s*\/\s*M\s*([\d.]+)$/i.exec(v);
    if (unisex) return gender === 'mens' ? unisex[2] : unisex[1];
    return v;
  }

  function syncSizes(panel) {
    var details = findDetails(panel, SIZE_KEY);
    if (!details) return;
    var original = details.querySelector(':scope > :not(summary) ul');
    if (!original) return;
    // Only touch the class when missing: classList.add always fires a mutation, which would re-run sync forever
    if (!original.classList.contains('ab-size-original')) original.classList.add('ab-size-original');

    var head = panel.querySelector(':scope > .ab-filter-head');
    var gender = head ? head.getAttribute('data-ab-gender') : '';
    if (gender !== 'mens' && gender !== 'womens') return;

    var groups = [];
    var byKey = {};
    original.querySelectorAll('input[type=checkbox]').forEach(function (input) {
      var key = sizeKey(sizeValue(input), gender);
      if (!byKey[key]) {
        byKey[key] = { inputs: [], key: key };
        groups.push(byKey[key]);
      }
      byKey[key].inputs.push(input);
    });
    if (!groups.length) return;

    var proxy = details.querySelector('.ab-size-proxy');
    if (!proxy) {
      groups.sort(function (a, b) {
        var x = parseFloat(a.key);
        var y = parseFloat(b.key);
        if (isNaN(x) || isNaN(y)) return isNaN(x) - isNaN(y);
        return x - y;
      });
      proxy = document.createElement('ul');
      proxy.className = 'ab-size-proxy';
      proxy.setAttribute('aria-label', gender === 'mens' ? "Men's sizes" : "Women's sizes");
      proxy.innerHTML = groups
        .map(function (g) {
          return (
            '<li><button type="button" class="ab-size-btn" aria-pressed="false" data-ab-size="' +
            g.key.replace(/"/g, '&quot;') + '">' + g.key.replace(/</g, '&lt;') + '</button></li>'
          );
        })
        .join('');
      original.after(proxy);
      details.classList.add('ab-size-merged');
    }

    proxy.querySelectorAll('.ab-size-btn').forEach(function (btn) {
      var group = byKey[btn.getAttribute('data-ab-size')];
      if (!group) return;
      var pressed = group.inputs.some(function (i) {
        return i.checked;
      });
      var disabled = group.inputs.every(function (i) {
        return i.disabled;
      });
      if (btn.getAttribute('aria-pressed') !== String(pressed)) btn.setAttribute('aria-pressed', String(pressed));
      if (btn.disabled !== disabled) btn.disabled = disabled;
    });

    // The applied count in the title counts raw values ("9" + "W10.5/M9" = 2); show sizes instead
    var count = details.querySelector(':scope > summary .text-base > span.ml-1');
    if (count) {
      var applied = groups.filter(function (g) {
        return g.inputs.some(function (i) {
          return initial.get(i) === true;
        });
      }).length;
      var text = '(' + applied + ')';
      if (count.textContent !== text) count.textContent = text;
    }
  }

  function toggleSize(btn) {
    var details = btn.closest('details');
    var panel = btn.closest(PANEL);
    var head = panel && panel.querySelector(':scope > .ab-filter-head');
    var gender = head ? head.getAttribute('data-ab-gender') : '';
    var key = btn.getAttribute('data-ab-size');
    var next = btn.getAttribute('aria-pressed') !== 'true';
    details.querySelectorAll('.ab-size-original input[type=checkbox]').forEach(function (input) {
      if (sizeKey(sizeValue(input), gender) !== key) return;
      if (next && input.disabled) return;
      input.checked = next;
    });
  }

  /* ---------- selected summary on collapsed rows ---------- */

  function selectedText(details) {
    var proxy = details.querySelector('.ab-size-proxy');
    if (proxy && getComputedStyle(proxy).display !== 'none') {
      return Array.prototype.map
        .call(proxy.querySelectorAll('[aria-pressed="true"]'), function (b) {
          return b.textContent;
        })
        .join(', ');
    }
    var checked = details.querySelectorAll(':scope > :not(summary) input:checked');
    return Array.prototype.map.call(checked, labelText).filter(Boolean).join(', ');
  }

  function syncSummary(details) {
    var title = details.querySelector(':scope > summary .text-base');
    if (!title) return;
    var out = title.querySelector('.ab-filter-selected');
    if (!out) {
      out = document.createElement('span');
      out.className = 'ab-filter-selected';
      title.appendChild(out);
    }
    var text = selectedText(details);
    if (out.textContent !== text) out.textContent = text;
  }

  /* ---------- bottom button label ---------- */

  function syncViewLabel(panel, dirty) {
    var btn = panel.querySelector('.ab-filter-bar__view');
    if (!btn) return;
    if (/^Updating/.test(btn.textContent)) return;
    if (dirty) {
      if (btn.textContent !== APPLY_LABEL) {
        btn.setAttribute('data-ab-label', btn.textContent);
        btn.textContent = APPLY_LABEL;
      }
    } else if (btn.hasAttribute('data-ab-label')) {
      if (btn.textContent === APPLY_LABEL) btn.textContent = btn.getAttribute('data-ab-label');
      btn.removeAttribute('data-ab-label');
    }
  }

  /* ---------- main sync ---------- */

  function sync() {
    queued = false;
    var isMobile = mobile.matches;
    var anyOpen = false;
    var panels = document.querySelectorAll(PANEL);

    panels.forEach(function (panel) {
      snapshot(panel);
      var visible = getComputedStyle(panel).display !== 'none';
      if (isMobile && visible) anyOpen = true;
      if (isMobile) syncSizes(panel);
      panel.querySelectorAll(':scope > details').forEach(function (details) {
        var want = isMobile ? userOpen.has(keyOf(details)) : true;
        if (details.open !== want) details.open = want;
        if (isMobile) syncSummary(details);
      });
      if (isMobile) syncViewLabel(panel, isDirty(panel));
    });

    if (anyOpen !== panelOpen) {
      panelOpen = anyOpen;
      document.documentElement.classList.toggle('ab-filter-open', anyOpen);
      if (anyOpen) {
        submitted = false;
        hideFloatingWidgets();
      } else {
        restoreFloatingWidgets();
        if (!submitted) {
          panels.forEach(function (panel) {
            if (isDirty(panel)) discard(panel);
          });
          queue();
        }
      }
    }
  }

  function queue() {
    if (queued) return;
    queued = true;
    Promise.resolve().then(sync);
  }

  /* ---------- events ---------- */

  // Hold every option change on mobile (the theme would reload the grid on each tap).
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      var panel = input && input.closest && input.closest(PANEL);
      if (panel && mobile.matches) {
        event.stopPropagation();
        if (input.matches('[data-price-range-checkbox]')) {
          // Same as the theme's handlePriceRangeChange, minus the submit
          panel.querySelectorAll('[data-price-range-checkbox]').forEach(function (cb) {
            if (cb !== input) cb.checked = false;
          });
          var min = panel.querySelector('[data-price-min-input]');
          var max = panel.querySelector('[data-price-max-input]');
          if (min && max) {
            min.value = input.checked ? input.getAttribute('data-range-min') : '';
            max.value = input.checked ? input.getAttribute('data-range-max') : '';
          }
        }
      }
      queue();
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      if (!mobile.matches || !event.target.closest) return;

      // Apply pending selections. The theme's own click handler closes the panel first; submitting
      // after that keeps the panel closed (the theme re-opens it after a re-render if it was open).
      var view = event.target.closest(PANEL + ' .ab-filter-bar__view');
      if (view) {
        var panel = view.closest(PANEL);
        if (isDirty(panel)) {
          submitted = true;
          setTimeout(function () {
            if (panel.isConnected) panel.requestSubmit();
          }, 0);
        }
        return;
      }

      var sizeBtn = event.target.closest(PANEL + ' .ab-size-btn');
      if (sizeBtn) {
        event.preventDefault();
        if (!sizeBtn.disabled) toggleSize(sizeBtn);
        queue();
        return;
      }

      // Row taps: drive the open state ourselves (the theme's icons follow a click counter)
      var summary = event.target.closest(PANEL + ' > details > summary');
      if (!summary) return;
      if (event.target.closest('button, a, input, label')) return; // e.g. the category's Reset link
      event.preventDefault();
      var details = summary.parentElement;
      var next = !details.open;
      var key = keyOf(details);
      if (next) userOpen.add(key);
      else userOpen.delete(key);
      details.open = next;
    },
    true
  );

  new MutationObserver(queue).observe(document.body, {
    attributeFilter: ['style', 'open', 'class'],
    attributes: true,
    childList: true,
    subtree: true,
  });
  mobile.addEventListener('change', queue);
  sync();

  /* ---------- floating widgets ---------- */
  // Discount teaser, AI shopping pill, chat: anything fixed in the bottom 200px of the screen,
  // outside main content, that is not a full-screen layer.
  var hidden = [];
  function hideFloatingWidgets() {
    hidden = [];
    var limit = window.innerHeight - 200;
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (/^(SCRIPT|MAIN|HEADER|STYLE|LINK)$/.test(el.tagName) || el.classList.contains('ab-qa')) return;
      var candidates = [el].concat(Array.prototype.slice.call(el.querySelectorAll(':scope > *, :scope > * > *')));
      for (var i = 0; i < candidates.length; i++) {
        var node = candidates[i];
        var style = getComputedStyle(node);
        if (style.position !== 'fixed' || style.visibility === 'hidden' || style.display === 'none') continue;
        var rect = node.getBoundingClientRect();
        if (!rect.width || !rect.height || rect.bottom < limit) continue;
        if (rect.height > window.innerHeight * 0.6) continue;
        hidden.push([node, node.style.visibility]);
        node.style.visibility = 'hidden';
        break;
      }
    });
  }
  function restoreFloatingWidgets() {
    hidden.forEach(function (entry) {
      entry[0].style.visibility = entry[1];
    });
    hidden = [];
  }
})();
